import * as React from "react";
import { WMSCapabilities } from "ol/format";
import { Tile as TileLayer } from "ol/layer";
import { TileWMS } from "ol/source";
import TileGrid from "ol/tilegrid/TileGrid";
import { createContext, Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { mapTileGrid } from "../Map/config";
import { LayerAction } from "../Map/enums";
import { getMap } from "../Map/GeoLiteMap";
import { getWmsCapabilities } from "../Map/rest";
import { useMapContext } from "./MapContext";

const parser = new WMSCapabilities();

export interface IWmsContext {
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
    url: string;
    setUrl: Dispatch<SetStateAction<string>>;
    onAddUrl: () => void;
    onToggle: (layer: IWmsState, id: string, layerAction: LayerAction) => void;
    onRemoveLayer: (id: string) => void;
}

export interface IWmsCapabilityLayer {
    Abstract: string;
    Attribution: string;
    Name: string;
    Style: [
        {
            Format: string;
            OnlineResource: string;
            size: number[];
        }
    ];
    Title: string;
    Layer?: IWmsCapabilityLayer[];
}

export interface IWmsState {
    id: string;
    title: string;
    name: string;
    expanded: boolean;
    visible: boolean;
    shown: boolean;
    sublayers?: IWmsState[];
}

const WmsContext = createContext<IWmsContext>(null);

export const useWmsContext = (): IWmsContext => useContext(WmsContext);

export const WmsContextProvider: FC = (props: React.PropsWithChildren<IWmsContext>) => {
    const { configA, wmsState, setWmsState } = useMapContext();

    const [loading, setLoading] = useState<boolean>(false);
    const [url, setUrl] = useState<string>("");

    useEffect(() => {
        wmsState && wmsState.map((state: IWmsState) => changeVisibility(state));
    }, [wmsState]);

    const onAddUrl = useCallback(async () => {
        setLoading(true);
        const capabilities = await getWmsCapabilities(url);
        setLoading(false);
        if (!capabilities) {
            return;
        }

        const wmsLayer: IWmsCapabilityLayer = parser.read(capabilities)?.Capability?.Layer;
        if (!wmsLayer) {
            return;
        }

        const id = "wms" + wmsLayer.Title + wmsLayer.Name;
        if (!wmsState.length || !wmsState.find((state) => state.id === id)) {
            const newState = [...wmsState, toState(wmsLayer)];
            setWmsState(newState);
            addWmsLayer(wmsLayer);
        }
    }, [url, wmsState]);

    const onToggle = useCallback(
        (layer: IWmsState, id: string, layerAction: LayerAction) => {
            const newState = [...wmsState];
            newState.map((state) => setLayerAction(state, layer, id, layerAction));
            setWmsState(newState);
        },
        [wmsState]
    );

    const toState = (layer: IWmsCapabilityLayer) => {
        const st = {
            id: "wms" + layer.Title + layer.Name,
            title: layer.Title,
            name: layer.Name,
            expanded: false,
            visible: false,
            shown: true
        };
        if (layer.Layer?.length) {
            st["sublayers"] = layer.Layer.map((sublayer) => ({
                id: "wms" + Math.random() * 23 + sublayer.Name,
                title: sublayer.Title,
                name: sublayer.Layer ? sublayer.Layer.map((sl) => sl.Name) : sublayer.Name,
                expanded: false,
                visible: false,
                shown: true
            }));
        }
        return st;
    };

    const addWmsLayer = (wmsLayer: IWmsCapabilityLayer) => {
        const configResolutions = configA.mapLods && configA.mapLods.map((lod) => lod.resolution);
        const defaultTileGrid = mapTileGrid;
        configResolutions && (defaultTileGrid.resolutions = configResolutions);
        const tileGrid = new TileGrid(defaultTileGrid);

        const olLayer = new TileLayer({
            source: new TileWMS({
                url: url,
                params: {
                    FORMAT: "image/vnd.jpeg-png8",
                    LAYERS: []
                },
                serverType: "geoserver",
                crossOrigin: "Anonymous",
                projection: "EPSG:3059",
                tileGrid,
                hidpi: false
            }),
            properties: { id: wmsLayer.Title, title: wmsLayer.Title },
            zIndex: 20000
        });
        olLayer.setVisible(true);

        getMap().addLayer(olLayer);
    };

    const removeWmsLayer = (id: string) => {
        const mapLayer = getMap()
            .getLayers()
            .getArray()
            .find((layer) => layer.get("id") === id);
        getMap().removeLayer(mapLayer);
    };

    const onRemoveLayer = useCallback(
        (id: string) => {
            removeWmsLayer(wmsState.find((state) => "wms" + state.title + state.name === id).title);
            setWmsState(wmsState.filter((state) => state.id !== id));
        },
        [wmsState]
    );

    const setLayerAction = (state: IWmsState, layer, id, layerAction) => {
        if (state.id === id) {
            layerAction === LayerAction.Expand && (state.expanded = !state.expanded);
            layerAction === LayerAction.View && (state.visible = !state.visible);
        } else {
            state.sublayers && state.sublayers.map((st) => setLayerAction(st, layer, id, layerAction));
        }
    };

    const changeVisibility = (state: IWmsState) => {
        const mapLayer = getMap()
            .getLayers()
            .getArray()
            .find((layer) => layer.get("id") === state.title) as TileLayer<TileWMS>;

        if (!mapLayer) {
            return;
        }

        // toggle main layer visibility
        !state.name && mapLayer.setOpacity(state.visible ? 1 : 0);

        // toggle sublayer visibility
        const layers = getVisibleLayers(state);
        mapLayer.getSource().updateParams({ LAYERS: layers });
        !layers.length && mapLayer.getSource().refresh();
    };

    const getVisibleLayers = (wmsState: IWmsState) => {
        const layers: string[] = [];

        const addVisibleLayers = (state: IWmsState) =>
            state.sublayers
                ? state.sublayers.map((sublayer) => addVisibleLayers(sublayer))
                : state.visible
                ? layers.push(state.name)
                : undefined;

        addVisibleLayers(wmsState);

        return layers;
    };

    const values = {
        loading,
        setLoading,
        url,
        setUrl,
        onAddUrl,
        onToggle,
        onRemoveLayer
    };

    return <WmsContext.Provider value={values}>{props.children}</WmsContext.Provider>;
};
